import { useState } from "react";
import axios from "axios";
import { X, Lock, Mail, User, Loader2, KeyRound } from "lucide-react";
import { useAuth, formatApiError } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// mode: "login" | "register" | "forgot"
export default function AuthModal({ open, onClose, initialMode = "login" }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  if (!open) return null;

  const reset = () => { setEmail(""); setPassword(""); setName(""); setError(""); setSuccess(""); };
  const switchMode = (m) => { reset(); setMode(m); };

  const close = () => { reset(); setMode(initialMode); onClose?.(); };

  const onLogin = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setSuccess("");
    try {
      await login(email.trim(), password);
      close();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onRegister = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setBusy(true); setError(""); setSuccess("");
    try {
      await register(email.trim(), password, name.trim());
      close();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const onForgot = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setSuccess("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() }, { timeout: 10000 });
      setSuccess("If that email exists, a reset link is on its way. Check your inbox.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/55 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={close}
      data-testid="auth-modal-backdrop"
    >
      <div
        className="bg-white w-full max-w-[440px] rounded-2xl shadow-2xl border border-[var(--line)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="auth-modal"
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-[var(--line)]">
          <div className="flex items-center gap-2">
            <span className="inline-flex w-8 h-8 rounded-full bg-[var(--gold)] items-center justify-center">
              <Lock size={14} className="text-white" />
            </span>
            <div className="text-[15px] font-black tracking-tight">
              {mode === "login" && "Welcome back"}
              {mode === "register" && "Create your account"}
              {mode === "forgot" && "Reset your password"}
            </div>
          </div>
          <button className="text-[var(--muted)] hover:text-black" onClick={close} aria-label="Close" data-testid="auth-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          {mode === "login" && (
            <form onSubmit={onLogin} className="space-y-3" data-testid="login-form">
              <Field icon={<Mail size={14} />} placeholder="Email" type="email" value={email} onChange={setEmail} testid="login-email" autoFocus />
              <Field icon={<Lock size={14} />} placeholder="Password" type="password" value={password} onChange={setPassword} testid="login-password" />
              {error && <ErrorBox text={error} testid="login-error" />}
              <button type="submit" disabled={busy || !email || !password} className="w-full bg-black text-white font-bold py-2.5 rounded-md text-[14px] hover:bg-[var(--ink)] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="login-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : null} Sign in
              </button>
              <div className="flex items-center justify-between text-[12px] pt-1">
                <button type="button" className="text-[var(--blue)] hover:underline font-semibold" onClick={() => switchMode("forgot")} data-testid="goto-forgot">
                  Forgot password?
                </button>
                <button type="button" className="text-[var(--muted)] hover:text-black" onClick={() => switchMode("register")} data-testid="goto-register">
                  Need an account? <span className="text-black font-bold">Sign up</span>
                </button>
              </div>
            </form>
          )}

          {mode === "register" && (
            <form onSubmit={onRegister} className="space-y-3" data-testid="register-form">
              <Field icon={<User size={14} />} placeholder="Your name (optional)" value={name} onChange={setName} testid="register-name" autoFocus />
              <Field icon={<Mail size={14} />} placeholder="Email" type="email" value={email} onChange={setEmail} testid="register-email" />
              <Field icon={<Lock size={14} />} placeholder="Password (8+ characters)" type="password" value={password} onChange={setPassword} testid="register-password" />
              {error && <ErrorBox text={error} testid="register-error" />}
              <button type="submit" disabled={busy || !email || password.length < 8} className="w-full bg-[var(--gold)] text-white font-bold py-2.5 rounded-md text-[14px] hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="register-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : null} Create account
              </button>
              <p className="text-[11px] text-[var(--muted)] leading-relaxed pt-1">
                By creating an account you agree to our editorial pillar: <strong>No drama. No conspiracies.</strong>
                We do not sell your email.
              </p>
              <div className="text-center text-[12px] pt-1">
                <button type="button" className="text-[var(--muted)] hover:text-black" onClick={() => switchMode("login")} data-testid="goto-login">
                  Already have an account? <span className="text-black font-bold">Sign in</span>
                </button>
              </div>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={onForgot} className="space-y-3" data-testid="forgot-form">
              <p className="text-[12.5px] text-[var(--muted)] leading-relaxed">
                Enter your email and we'll send a secure reset link valid for 30 minutes.
              </p>
              <Field icon={<Mail size={14} />} placeholder="Email" type="email" value={email} onChange={setEmail} testid="forgot-email" autoFocus />
              {error && <ErrorBox text={error} testid="forgot-error" />}
              {success && <SuccessBox text={success} testid="forgot-success" />}
              <button type="submit" disabled={busy || !email} className="w-full bg-black text-white font-bold py-2.5 rounded-md text-[14px] hover:bg-[var(--ink)] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="forgot-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Send reset link
              </button>
              <div className="text-center text-[12px] pt-1">
                <button type="button" className="text-[var(--muted)] hover:text-black" onClick={() => switchMode("login")} data-testid="goto-login-from-forgot">
                  Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon, placeholder, value, onChange, type = "text", testid, autoFocus }) {
  return (
    <label className="flex items-center gap-2 border border-[var(--line)] rounded-md px-3 py-2 focus-within:border-black bg-white">
      <span className="text-[var(--muted)]">{icon}</span>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        className="w-full text-[14px] outline-none border-0 bg-transparent"
        data-testid={testid}
      />
    </label>
  );
}

function ErrorBox({ text, testid }) {
  return <div className="text-[12.5px] text-[var(--red)] bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid={testid}>{text}</div>;
}

function SuccessBox({ text, testid }) {
  return <div className="text-[12.5px] text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-2" data-testid={testid}>{text}</div>;
}
